import React, { useEffect, useLayoutEffect, useState } from 'react';
import { PhoneCross } from '@saleshandy/icons';
import classNames from 'classnames';

import Input from '../../../../../../../shared/design-system/components/input';
import Modal from '../../../../../../../shared/design-system/components/atoms/modal';
import RadioButtonGroup from '../../../../../../../shared/design-system/components/radio-button-group';
import AgencyClientDropdown from '../../../../../../agency-client-management/components/agency-client-dropdown';
import Alert from '../../../../../../../shared/design-system/ui/alert';

import { DoNotCallListApplicableTo } from '../../../../../enums/do-not-call-list';
import {
  CreateDoNotCallListPayload,
  DoNotCallList,
  ModalActions,
  UpdateDoNotCallListPayload,
} from '../../../../../types/do-not-call-list';
import { AgencyClientFilter } from '../../../../../../agency-client-management/types/types';
import { AgencyClientStatus } from '../../../../../../agency-client-management/enums/agency-client';
import { getClientName } from '../../../../../../agency-client-management/helpers/helpers';
import hasPermission from '../../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../../shared/utils/access-control/enums/permissions';
import { isAgency } from '../../../../../../../shared/utils/user-details';

type IProps = {
  show: boolean;
  action: ModalActions;
  defaultDoNotCallList: DoNotCallList;
  isLoading: boolean;
  agencyClients: AgencyClientFilter[];
  onClose: VoidFunction;
  onAddDoNotCallList: (payload: CreateDoNotCallListPayload) => void;
  onUpdateDoNotCallList: (payload: UpdateDoNotCallListPayload) => void;
  sendGetAgencyClientsDropdownRequest: (payload: {
    status: AgencyClientStatus;
    search: string;
  }) => void;
};

const defaultClient: AgencyClientFilter = {
  id: -1,
  firstName: '',
  lastName: '',
  email: '',
  companyName: '',
  status: AgencyClientStatus.Active,
};

const AddModifyDoNotCallList: React.FC<IProps> = ({
  show,
  action,
  defaultDoNotCallList,
  isLoading,
  agencyClients,
  onClose,
  onAddDoNotCallList,
  onUpdateDoNotCallList,
  sendGetAgencyClientsDropdownRequest,
}) => {
  const [modalMeta, setModalMeta] = useState({
    title: 'Add List',
    submitButtonText: 'Add',
  });

  const [listName, setListName] = useState('');
  const [listNameError, setListNameError] = useState('');
  const [applicableTo, setApplicableTo] = useState<DoNotCallListApplicableTo>(
    DoNotCallListApplicableTo.AllClients,
  );
  const [selectedClient, setSelectedClient] = useState<AgencyClientFilter>(
    defaultClient,
  );

  const isAgencyUser = hasPermission(Permissions.CLIENT_READ) && isAgency();

  const validateName = (value: string): string => {
    if (!value || !value.trim()) return 'List name is required';
    if (value.trim().length > 100)
      return 'List name must be 100 characters or fewer';
    return '';
  };

  const hasErrors = () => validateName(listName) !== '';

  const isSaveDisabled = (): boolean => {
    if (isLoading || hasErrors()) return true;
    if (action === 'modify') {
      const nameUnchanged = listName.trim() === defaultDoNotCallList?.name;
      const defaultApplicableTo = defaultDoNotCallList?.client
        ? DoNotCallListApplicableTo.SpecificClient
        : DoNotCallListApplicableTo.AllClients;
      const applicableToUnchanged = applicableTo === defaultApplicableTo;
      const defaultClientId = defaultDoNotCallList?.client?.id ?? -1;
      const clientIdUnchanged = selectedClient.id === defaultClientId;
      return nameUnchanged && applicableToUnchanged && clientIdUnchanged;
    }
    return false;
  };

  const onInputChange = (
    value: string,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    e.preventDefault();
    setListName(value);
    if (listNameError) setListNameError(validateName(value));
  };

  const onInputBlur = () => {
    setListNameError(validateName(listName));
  };

  const handleSubmit = () => {
    const error = validateName(listName);
    if (error) {
      setListNameError(error);
      return;
    }

    if (action === 'add') {
      const payload: CreateDoNotCallListPayload = { name: listName.trim() };
      if (isAgencyUser) {
        if (
          applicableTo === DoNotCallListApplicableTo.SpecificClient &&
          selectedClient.id !== -1
        ) {
          payload.clientId = selectedClient.id;
        }
      }
      onAddDoNotCallList(payload);
      return;
    }

    if (action === 'modify') {
      const payload: UpdateDoNotCallListPayload = {
        dncListId: defaultDoNotCallList.id,
        name: listName.trim(),
      };
      if (isAgencyUser) {
        payload.makeGlobal =
          applicableTo === DoNotCallListApplicableTo.AllClients;
        if (
          applicableTo === DoNotCallListApplicableTo.SpecificClient &&
          selectedClient.id !== -1
        ) {
          payload.clientId = selectedClient.id;
        }
      }
      onUpdateDoNotCallList(payload);
    }
  };

  const renderRadioButton = (title: string) => (
    <div className="radio-input-btn">
      <h2>{title}</h2>
    </div>
  );

  const helperText =
    applicableTo === DoNotCallListApplicableTo.AllClients && isAgencyUser
      ? "All prospects whose numbers match this list will be marked as Do Not Call globally (including client sequences). You won’t be able to call them anywhere, and call-related sequence tasks will not be executed."
      : "All prospects whose numbers match this list will be marked as Do Not Call. You won’t be able to make calls from call tasks associated with this client’s sequences.";

  const helperAlertClassName = classNames([
    'helper-alert-warning-wrapper',
    { 'rounded-text-color': !isAgencyUser },
  ]);

  useLayoutEffect(() => {
    if (show && action === 'modify') {
      setModalMeta({ title: 'Modify List', submitButtonText: 'Save' });
    }
  }, [show]);

  useEffect(() => {
    if (show && defaultDoNotCallList) {
      setListName(defaultDoNotCallList.name);
      if (action === 'modify') {
        const initialApplicableTo = defaultDoNotCallList.client
          ? DoNotCallListApplicableTo.SpecificClient
          : DoNotCallListApplicableTo.AllClients;
        setApplicableTo(initialApplicableTo);
        if (defaultDoNotCallList.client) {
          setSelectedClient({
            ...defaultDoNotCallList.client,
            status: AgencyClientStatus.Active,
          });
        }
      }
    }
  }, [show, defaultDoNotCallList]);

  useEffect(() => {
    if (!show) {
      setModalMeta({ title: 'Add List', submitButtonText: 'Add' });
      setListName('');
      setListNameError('');
      setApplicableTo(DoNotCallListApplicableTo.AllClients);
      setSelectedClient(defaultClient);
    }
  }, [show]);

  useEffect(() => {
    if (show && isAgencyUser) {
      sendGetAgencyClientsDropdownRequest({
        status: AgencyClientStatus.Active,
        search: '',
      });
    }
  }, [show]);

  return (
    <Modal
      show={show}
      className="h-100 add-modify-dnc-list-modal"
      onClose={onClose}
      hideFooter={false}
      submitButtonText={modalMeta.submitButtonText}
      backdrop="static"
      hideHeader={true}
      onSubmit={handleSubmit}
      cancelButtonClassName="cancel-btn"
      submitButtonClassName="submit-btn"
      isSubmitDisabled={isSaveDisabled()}
      isSubmitLoading={isLoading}
      extraModalProps={{ enforceFocus: false }}
    >
      <div className="add-modify-dnc-list-modal--container">
        <div className="title-container d-flex align-items-center">
          <PhoneCross width={24} height={24} />
          <h5>{modalMeta.title}</h5>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <Input
            name="listName"
            label="List Name"
            placeholder="E.g. USA Blacklist"
            value={listName}
            variant={listNameError ? Input.Variant.Error : undefined}
            caption={listNameError}
            onChange={onInputChange}
            onBlur={onInputBlur}
            autoFocus
          />

          {isAgencyUser && (
            <>
              <div className="radio-input-wrapper">
                <h1>Applicable to</h1>
                <div className="radio-input-container">
                  <RadioButtonGroup
                    name="applicableTo"
                    value={applicableTo}
                    options={[
                      {
                        label: renderRadioButton('All Clients (Global)'),
                        value: DoNotCallListApplicableTo.AllClients,
                        disabled: false,
                      },
                      {
                        label: renderRadioButton('Specific Client'),
                        value: DoNotCallListApplicableTo.SpecificClient,
                        disabled: false,
                      },
                    ]}
                    onChange={setApplicableTo}
                  />
                </div>
              </div>

              {applicableTo === DoNotCallListApplicableTo.SpecificClient && (
                <AgencyClientDropdown
                  options={agencyClients}
                  initialSelectedOption={selectedClient}
                  className="agency-select-dropdown"
                  selectedOptionClassName="w-100"
                  onChange={(option) => {
                    if (option) setSelectedClient(option);
                  }}
                  optionRenderer={(option) => (
                    <div className="d-flex align-items-center">
                      <span>
                        {getClientName(option, 60, {
                          company: true,
                          email: true,
                          deleted: true,
                        })}
                      </span>
                    </div>
                  )}
                  disabled={false}
                />
              )}
            </>
          )}

          <Alert
            variant="warning"
            description={<>{helperText}</>}
            className={helperAlertClassName}
          />
        </form>
      </div>
    </Modal>
  );
};

export default AddModifyDoNotCallList;
